import axios from "axios";
import route from "@/router.js";
import urls from "@/constants/urls.js";
import { routeUserAfterLogin } from "@/helpers/userRoute";
import { markFreshSignIn } from "@/helpers/new-dashboard-offer.js";

function convertExpDate(expDate) {
  if (!isNaN(expDate) && typeof expDate === "number") {
    return expDate;
  }

  try {
    const date = new Date(expDate);
    const timestamp = Math.floor(date.getTime() / 1000);
    return timestamp;
  } catch (error) {
    console.error("Сonvert ExpDate error", error);
    return null;
  }
}

const state = {
  user: null,
  errorLogin: [],
  resetPasswordSuccess: false,
};

const mutations = {
  setUser(state, value) {
    if (!value.attributes.require_2fa_code && value.attributes.token) {
      localStorage.setItem("orgId", value.id);
      localStorage.setItem("c_t", value.attributes.token);
      localStorage.setItem("jwt_token", value.attributes.jwt_token);
      localStorage.setItem("type", value.type);
      const t = new Date().getTime() + 1000 * 60 * 60 * 24 * 2;
      const expDate = new Date(convertExpDate(value.attributes.exp_date) * 1000 || t);
      const token = value.attributes.jwt_token;
      document.cookie = `jwt_token=${token}; domain=.charidy.com; path=/; expires=${expDate};`;
      // A sign-in, not a session taken over from the new dashboard (that one has
      // no `token`): the router may still send the organization there.
      markFreshSignIn();
    }
    state.user = value;
  },

  unsetUser(state) {
    state.user = null;
  },

  setErrorLogin(state, value) {
    state.errorLogin = value;
  },

  setResetPasswordSuccess(state, value) {
    state.resetPasswordSuccess = value;
  },
};

const getters = {
  user: (state) => () => state.user,

  require2FACode: (state) => state.user?.attributes?.require_2fa_code || false,

  isLoggedIn(state) {
    return state.user !== null;
  },

  token: (state) => () => {
    if (state.user != null && state.user.attributes != null) {
      return state.user.attributes.token;
    }
    return "";
  },
};

const actions = {
  unsetUser({ commit }) {
    axios.post(urls.logout()).then(() => {
      localStorage.removeItem("orgId");
      localStorage.removeItem("c_t");
      localStorage.removeItem("jwt_token");
      localStorage.removeItem("type");
      localStorage.removeItem("navChoice");
      document.cookie = `jwt_token=; domain=.charidy.com; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/`;
      commit("setOrganizations", []);
      commit("setAccount", []);
      commit("setOrgId", null, { root: true });
      commit("unsetUser");

      // Best-effort Facebook session teardown so that the next click on the FB button
      // doesn't silently re-authenticate from a stale browser session.
      try {
        if (window.FB && typeof window.FB.getLoginStatus === "function") {
          window.FB.getLoginStatus((response) => {
            if (response?.status === "connected") {
              window.FB.logout();
            }
          });
        }
      } catch (err) {
        console.warn("FB.logout cleanup failed", err);
      }
    });
  },

  async checkToken({ commit, state }) {
    const token = route.currentRoute.params.token || route.currentRoute.query.token;
    let currentPath = route.currentRoute.path;
    let nextPath = "";

    // TODO: if user check allow dashboard access
    if (currentPath !== "/") {
      if (route.currentRoute.meta.donorDashboard) {
        nextPath = "/donor-dashboard";
      } else {
        nextPath = "/dashboard";
      }
    }

    if (token) {
      try {
        const response = await axios.get(urls.login, { params: { token: token } });
        let user = response.data.data;
        commit("setUser", user);
        const navChoice = localStorage.getItem("navChoice");
        if (navChoice || nextPath) {
          // When a token is present (indicating a Charidy redirect),
          // `nextPath` should take precedence over `navChoice` for routing logic.
          await route.push({ path: nextPath ? nextPath : navChoice });
        }
        return;
      } catch {
        localStorage.removeItem("orgId");
        localStorage.removeItem("c_t");
        localStorage.removeItem("type");
        commit("setOrganizations", []);
        commit("setAccount", []);
        commit("errorLogin", ["Wrong token"]);
        commit("setOrgId", null, { root: true });
      }
    } else if (route.currentRoute.meta.SignIn && state.user) {
      if (route.currentRoute.query.redirect) {
        route.push({ path: route.currentRoute.query.redirect });
        return;
      }

      const navChoice = localStorage.getItem("navChoice");
      if (navChoice || nextPath) {
        await route.push({ path: navChoice ? navChoice : nextPath });
      }
      return;
    }

    return Promise.resolve();
  },

  loginUser({ commit, dispatch }, options) {
    commit("setErrorLogin", []);

    const rawCredential = options[0];
    const { noRouting = false, googleLogin = false, facebookLogin = false } = options[1] || {};
    const isSocialLogin = googleLogin || facebookLogin;

    let data = rawCredential;
    let url = urls.login;

    if (googleLogin) {
      url = urls.googleLogin;
      data = {
        credential: data,
      };
    } else if (facebookLogin) {
      url = urls.facebookLogin;
      data = {
        access_token: data,
      };
    }

    const handleSuccess = (response) => {
      let user = {};
      if (isSocialLogin) {
        user.attributes = response.data.data;
        user.id = response.data.data?.id;
        user.type = "";
      } else {
        user = response.data.data;
      }

      // OTP token response (step 1 of email/phone OTP flow) — don't route,
      // just return user data so the caller can capture the token
      if (user?.attributes?.otp_code_token && !user?.attributes?.token) {
        return { user };
      }

      commit("setOrgId", user.id, { root: true });
      commit("setUser", user);
      dispatch("getLanguages");

      if (noRouting) {
        return { user };
      }

      return routeUserAfterLogin(user);
    };

    const handleFailure = (e) => {
      console.log("e", e);
      if (e.response?.data) {
        if (e.response.data.error) {
          const data = e.response.data;

          commit("setErrorLogin", [data.error_translation_key, data.error]);
        } else if (e.response.data.errors) {
          commit("setErrorLogin", [e.response.data.errors?.[0]?.message || "Invalid credentials"]);
        } else {
          commit("setErrorLogin", [e.response.data]);
        }
      }
    };

    return axios
      .post(url, data)
      .then(handleSuccess)
      .catch((e) => {
        // Facebook auto-signup: when the backend returns 404 (no Charidy login matches the
        // Facebook account's email), transparently create a donor account using the same
        // Facebook access token and re-attempt the login. The /orgarea/api/v1/signup
        // endpoint validates the token server-side, skips captcha and auto-verifies the
        // resulting login, so this is a single round-trip with no extra UI surface.
        if (facebookLogin && e?.response?.status === 404 && rawCredential) {
          return axios
            .post(urls.signup, {
              facebook_oauth_token: rawCredential,
            })
            .then(() => axios.post(url, data).then(handleSuccess).catch(handleFailure))
            .catch(handleFailure);
        }
        handleFailure(e);
      });
  },

  resetPassword({ commit }, data) {
    commit("setErrorLogin", []);
    commit("setResetPasswordSuccess", false);

    axios
      .post(urls.resetPassword, data)
      .then((response) => {
        if (response.data.Result == "ok") {
          commit("setResetPasswordSuccess", true);
        } else {
          commit("setErrorLogin", ["Email not found"]);
        }
      })
      .catch((e) => {
        if (e.response) {
          const data = e.response.data;

          commit("setErrorLogin", [data.error_translation_key, data.error]);
        }
      });
  },

  clearError({ commit }) {
    commit("setErrorLogin", []);
  },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
